import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { apiService, Operatory } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const emptyOperatory: Partial<Operatory> = {
  name: '',
  description: '',
  is_active: true,
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const OperatoriesPage: React.FC = () => {
  const [operatories, setOperatories] = useState<Operatory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Operatory> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getOperatories(false);
      setOperatories(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || 'Behandlungsräume konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name) {
      setError('Name ist Pflicht');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.id) {
        await apiService.updateOperatory(editing.id, editing);
      } else {
        await apiService.createOperatory(editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (o: Operatory) => {
    if (!window.confirm(`Behandlungsraum „${o.name}" wirklich löschen?`)) return;
    try {
      await apiService.deleteOperatory(o.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Stammdaten"
        title="Behandlungsräume"
        description="Räume und ihre Ausstattung pflegen"
        actions={
          <button
            onClick={() => setEditing({ ...emptyOperatory })}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
            Neuer Raum
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
        ) : operatories.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Noch keine Räume"
            description="Lege Behandlungsräume an, um Termine räumlich zu planen."
            action={
              <button
                onClick={() => setEditing({ ...emptyOperatory })}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                Ersten Raum anlegen
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-border">
              <thead className="bg-brand-surface-2/60">
                <tr>
                  <Th>Name</Th>
                  <Th>Beschreibung</Th>
                  <Th>Status</Th>
                  <Th align="right"><span className="sr-only">Aktionen</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {operatories.map(o => (
                  <tr
                    key={o.id}
                    className={`hover:bg-brand-surface-2/60 transition-colors ${o.is_active ? '' : 'opacity-60'}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-brand-text">{o.name}</td>
                    <td className="px-4 py-3 text-sm text-brand-text">{o.description}</td>
                    <td className="px-4 py-3 text-sm">
                      {o.is_active
                        ? <StatusPill tone="success" dot>Aktiv</StatusPill>
                        : <StatusPill tone="neutral">Inaktiv</StatusPill>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <IconButton onClick={() => setEditing({ ...o })} label="Bearbeiten" tone="primary">
                          <Pencil className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                        <IconButton onClick={() => onDelete(o)} label="Löschen" tone="danger">
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border max-w-lg w-full p-6">
            <h3 className="text-base font-semibold mb-5 text-brand-text">
              {editing.id ? 'Raum bearbeiten' : 'Neuer Raum'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">Name *</label>
                <input
                  type="text"
                  value={editing.name || ''}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                  placeholder="z. B. Behandlungszimmer 1"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1">Beschreibung</label>
                <textarea
                  value={editing.description || ''}
                  onChange={e => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                  placeholder="z. B. Ausstattung, Lage, Hinweise"
                  className={inputClass}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                  className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                />
                <span>Aktiv</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-brand-border">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                Abbrechen
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}>
    {children}
  </th>
);

const IconButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'primary' | 'danger';
}> = ({ children, onClick, label, tone }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400'
      : 'text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10';
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${toneClass}`}
    >
      {children}
    </button>
  );
};

export default OperatoriesPage;
